/**
 * Base Settings Field Components
 * Reusable form fields for block settings
 */

import React from "react";
import { Box, Typography, Slider as MuiSlider } from "@mui/material";
import {
  labelStyle,
  inputStyle,
  textareaStyle,
  selectStyle,
  colorInputStyle,
  checkboxContainerStyle,
  checkboxStyle,
} from "../../../config/settingsStyles";

// ============================================
// Text Field
// ============================================
interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "url" | "email";
  disabled?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{ ...inputStyle, ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
    />
  </div>
);

// ============================================
// Number Field
// ============================================
interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={min}
      max={max}
      step={step}
      style={inputStyle}
    />
  </div>
);

// ============================================
// Textarea Field
// ============================================
interface TextareaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export const TextareaField: React.FC<TextareaFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={textareaStyle}
    />
  </div>
);

// ============================================
// Select Field
// ============================================
interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}

export const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// ============================================
// Slider Field
// ============================================
interface SliderFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
}

export const SliderField: React.FC<SliderFieldProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit = "",
}) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: "#374151" }}>
      {label}: {value}
      {unit}
    </Typography>
    <MuiSlider
      value={value}
      onChange={(_, v) => onChange(v as number)}
      min={min}
      max={max}
      step={step}
      size="small"
      valueLabelDisplay="auto"
    />
  </Box>
);

// ============================================
// Color Field
// ============================================
interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  showReset?: boolean;
  defaultValue?: string;
}

export const ColorField: React.FC<ColorFieldProps> = ({
  label,
  value,
  onChange,
  showReset = false,
  defaultValue,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...colorInputStyle, flex: showReset ? undefined : 1 }}
      />
      {showReset && defaultValue && (
        <button
          onClick={() => onChange(defaultValue)}
          style={{
            padding: "8px 12px",
            backgroundColor: "#f3f4f6",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "0.75rem",
          }}
        >
          Reset
        </button>
      )}
    </div>
  </div>
);

// ============================================
// Checkbox Field
// ============================================
interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({ label, checked, onChange, id }) => {
  const inputId = id || `checkbox-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div style={checkboxContainerStyle}>
      <input
        type="checkbox"
        id={inputId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={checkboxStyle}
      />
      <label htmlFor={inputId} style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
        {label}
      </label>
    </div>
  );
};

// ============================================
// Settings Group (category with header)
// ============================================
interface SettingsGroupProps {
  title: string;
  children: React.ReactNode;
}

export const SettingsGroup: React.FC<SettingsGroupProps> = ({ title, children }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        padding: "8px 12px",
        backgroundColor: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </Typography>
    </div>
    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
      {children}
    </div>
  </div>
);

// ============================================
// Settings Section (wrapper)
// ============================================
interface SettingsSectionProps {
  title?: string;
  children: React.ReactNode;
  collapsible?: boolean;
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({ title, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
    {title && (
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {title}
      </Typography>
    )}
    {children}
  </div>
);

// ============================================
// Settings Row (2 columns)
// ============================================
interface SettingsRowProps {
  children: React.ReactNode;
}

export const SettingsRow: React.FC<SettingsRowProps> = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>{children}</div>
);
